import type { format as FormatFn } from "date-fns";
import config from "./config";
import locale from "./locale";
import { LtProxy } from "./appChannel";
import { storage, seStorage } from "./storage";
// 导入 request 函数
import {
  removeToken as requestRemoveToken,
  appendToken as requestAppendToken,
  appendUrl as requestAppendUrl,
  combineObject as requestCombineObject,
  combineObjects as requestCombineObjects,
  apiRequest,
} from "./request";
import { InternalAxiosRequestConfig } from "axios";
import { DeviceType } from "../types/common";
import { md5 } from "js-md5";
import { getCurrentLanguage, translate } from "../i18n/utils";

// 类型定义
interface StatUrlResult {
  inner: boolean;
  url: string;
}

interface ImageUrlProps {
  width?: number;
  height?: number;
  mode?: string;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  alert?: boolean;
  ignore?: boolean;
  noLogin?: boolean;
  headers?: Record<string, string>;
  body?: any;
}

interface RequestResponse {
  status: number;
  message?: string;
  time?: number;
  user?: any;
  [key: string]: any;
}

class Utils {
  public forceMobileLayout: boolean = true;
  public runMap: Record<string, boolean> = {};
  public directCids: Record<string, any> = {};
  public localeList: Record<string, boolean> = {};
  public keepDataKeys: string[] = [
    "currentCountry",
    "currentLanguage",
    "countryId",
    "selectedCountryId",
    "currencyId",
    "agreement",
    "aid",
    "gaid",
    "inApp",
    "version",
    "supportPwa",
    "pwa",
    "checkAge",
    "fail_records_login",
    "fail_records_register",
    "webPush-dismissed-date",
    "has_registered",
    "recharge-offer-no-first-recharge:",
    "recharge_promo_shown:",
    "downloadAppPopup-dismissed-date",
    "shown_popup_records",
    "skip_pwa_guide_page",
    "a2hsFirstOpen_sent",
    "landingAttributionReported",
  ];
  decrypt = (data: string, startupData?: any): string => {
    const cryptoKey = this.getCryptoKey(startupData);
    if (!cryptoKey) return data;
    const keyUtf8 = CryptoJS.enc.Utf8.parse(cryptoKey);
    const ciphertext = CryptoJS.enc.Hex.parse(data);
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: ciphertext,
    });
    const decrypted = CryptoJS.AES.decrypt(cipherParams, keyUtf8, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  };
  encrypt = (data: string, startupData?: any): string => {
    const cryptoKey = this.getCryptoKey(startupData);
    if (!cryptoKey) return data;
    const aesKey = CryptoJS.enc.Utf8.parse(cryptoKey);
    const encrypted = CryptoJS.AES.encrypt(data, aesKey, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.ciphertext.toString().toUpperCase();
  };
  cryptoUrl: Record<string, string> = {
    "/api/game/bet": "/api/game/v1/bet",
    "/api/game/bets": "/api/game/v1/bets",
    "/api/game/bet_result": "/api/game/v1/bet_result",
    "/api/game/bet_record": "/api/game/v1/bet_record",
    "/api/game/finish_hold": "/api/game/v1/finish_hold",
    "/api/game/finish_all_my_hold": "/api/game/v1/finish_all_my_hold",
    "/api/game/crash/stop": "/api/game/crash/v1/stop",
    "/api/game/aviator/stop": "/api/game/aviator/v1/stop",
    "/api/football_game/play": "/api/football_game/v1/play",
    "/api/football_game/cash_out": "/api/football_game/v1/cash_out",
    "/api/kangaroo_game/play": "/api/kangaroo_game/v1/play",
    "/api/kangaroo_game/cash_out": "/api/kangaroo_game/v1/cash_out",
    "/api/mines_game/play": "/api/mines_game/v1/play",
    "/api/mines_game/cash_out": "/api/mines_game/v1/cash_out",
    "/api/stair_game/play": "/api/stair_game/v1/play",
    "/api/stair_game/cash_out": "/api/stair_game/v1/cash_out",
    "/api/thimbles_game/play": "/api/thimbles_game/v1/play",
    "/api/tower_game/play": "/api/tower_game/v1/play",
    "/api/tower_game/cash_out": "/api/tower_game/v1/cash_out",
  };
  getCryptoUrl = (url: string, startupData?: any) => {
    const cryptoKey = this.getCryptoKey(startupData);
    if (!cryptoKey) return url;
    return this.cryptoUrl[url];
  };
  isCryptoRequest(url: string) {
    return Object.values(this.cryptoUrl).includes(url);
  }
  cryptoRequest(options: InternalAxiosRequestConfig<any>) {
    options.headers["SE"] = true;
    options.data = this.encrypt(options.data);
  }
  getDeviceId() {
    try {
      if (window.AndroidBridge?.getDeviceId) {
        return window.AndroidBridge.getDeviceId();
      }
      return null;
    } catch (e) {
      console.error("getDeviceId error:", e);
      return null;
    }
  }
  onInit(): void {
    let gaid = this.getQueryString("gaid");
    if (!gaid) {
      gaid = this.getQueryString("idfa");
    }
    let aid = this.getQueryString("aid");
    if (!aid) {
      aid = this.getQueryString("idfv");
    }
    if (gaid) {
      this.setObject("gaid", gaid);
    }
    if (aid) {
      this.setObject("aid", aid);
    }
    const version = this.getQueryString("version");
    if (version) {
      this.setObject("version", version);
    }
    const inApp = this.getQueryString("inApp");
    if (inApp) {
      this.setObject("inApp", inApp);
    }
    const gpApp = this.getQueryString("gpApp");
    if (gpApp) {
      this.setObject("gpApp", gpApp);
    }
  }

  initConfig = () => {
    try {
      const tkk = this.getQueryString("tkk");
      if (tkk) {
        this.setObject("tkk", tkk);
      } else {
        this.setObject("tkk", null);
      }
    } catch {
      // 忽略错误
    }
  };
  private _fontSizeResizeHandler: (() => void) | null = null;

  getBestFontSizePx(px: number) {
    const baseWidth = 375;
    const maxWidth =
      window.innerHeight <= 900 && window.innerWidth > 486 ? 375 : 486;
    const currentWidth = Math.min(window.innerWidth, maxWidth);
    const scale = currentWidth / baseWidth;
    return scale * px;
  }
  getProperFontSize() {
    return this.getBestFontSizePx(16) + "px";
  }
  private _applyFontSize() {
    const fontSize = this.getProperFontSize();
    const htmlEle = document.documentElement;
    htmlEle.style.setProperty("font-size", fontSize);
  }
  initGlobalFontSizeToHtml() {
    this._applyFontSize();

    if (!this._fontSizeResizeHandler) {
      let rafId: number | null = null;
      this._fontSizeResizeHandler = () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          this._applyFontSize();
          rafId = null;
        });
      };
      window.addEventListener("resize", this._fontSizeResizeHandler);
    }
  }
  countToArray(count: number, defaultValue?: number): number[] {
    try {
      count = parseInt(count.toString());
    } catch {
      // 忽略错误
    }
    if (!count) {
      count = defaultValue || 0;
    }
    if (!count || count < 0) {
      return [];
    }
    const ret: number[] = [];
    for (let i = 0; i < count; i++) {
      ret.push(i);
    }
    return ret;
  }

  statUrl(url: string): StatUrlResult {
    if (url.indexOf("#gameId=") === 0) {
      return {
        inner: false,
        url: this.appendToken("/api/game/play?" + url.substring(1)),
      };
    } else if (url[0] === "/") {
      return { inner: true, url: url };
    } else {
      return { inner: false, url: url };
    }
  }

  cssSize(size: number | string): string | undefined {
    if (size === 0) {
      return "0px";
    }
    let sizeStr = size + "";
    sizeStr = sizeStr.toLowerCase().trim();
    if (!sizeStr) return;
    if (sizeStr.indexOf("px") > 0) {
      sizeStr = sizeStr.substring(0, sizeStr.length - 2);
    }
    const n = Number(sizeStr);
    if (!n) return sizeStr;
    return n + "px";
  }

  checkIsIos(): boolean {
    return (
      [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod",
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  }

  isSafari() {
    try {
      const ua = (navigator?.userAgent || "").toLowerCase();

      // 检测已知的应用内浏览器
      const inAppBrowser =
        /fbav|fban|fb_iab/.test(ua) || // Facebook
        /instagram/.test(ua) || // Instagram
        /micromessenger/.test(ua) || // WeChat
        /line\//.test(ua) || // Line
        /tiktok|musical_ly|aweme|bytedancewebview/.test(ua) || // TikTok/抖音
        /telegram|tga|twa/.test(ua); // Telegram

      // 检测通用 WebView 特征
      const isWebView =
        /; wv\)/.test(ua) || // Android WebView 标记
        /\bwv\b/.test(ua) || // WebView 标记
        /version\/\d+.*chrome/.test(ua) || // Android WebView 特征
        // iOS 仅使用明确桥接特征，避免把 Safari/Chrome 误判为 WebView
        !!(window as any).webkit?.messageHandlers?.openExternalBrowser ||
        !!(window as any).webkit?.messageHandlers?.openInBrowser ||
        !!(window as any).flutter_inappwebview || // Flutter WebView
        !!(window as any).ReactNativeWebView || // React Native WebView
        !!(window as any).android || // Android WebView bridge
        !!(window as any).AndroidBridge; // 自定义 Android bridge

      // 如果在应用内浏览器或 WebView 中，不是 Safari
      if (inAppBrowser || isWebView) {
        return false;
      }

      // 检测 Safari 特征
      const hasSafari = /safari/.test(ua);
      const hasChrome = /chrome/.test(ua) || /crios/.test(ua);

      // 真正的 Safari：有 Safari 标识且没有 Chrome 标识
      return hasSafari && !hasChrome;
    } catch {
      return false;
    }
  }
  getEnvInfo() {
    const ua = (navigator?.userAgent || "").toLowerCase();
    const isAndroid = /android/.test(ua);
    const isiOS = /iphone|ipad|ipod/.test(ua);

    // 最先判断：APK 原生 App 环境
    // APK 内嵌 WebView 的 UA 也包含 wv / window.android 等标记，但不应被视为 InAppWebView
    // isApp() 依赖 localStorage（gaid/aid）或 bridge 对象，首次加载时可能还没初始化
    // 因此额外检查 URL 参数和 flutter/RN bridge 作为补充
    const isNativeApp =
      this.isApp() ||
      !!this.getQueryString("gaid") ||
      !!this.getQueryString("aid") ||
      !!this.getQueryString("idfv") ||
      !!this.getQueryString("idfa") ||
      typeof (window as any).flutter_inappwebview !== "undefined" ||
      typeof (window as any).ReactNativeWebView !== "undefined";

    const inFB = /fbav|fban|fb_iab/.test(ua);
    const inIG = /instagram/.test(ua);
    const inWeChat = /micromessenger/.test(ua);
    const inLine = /line\//.test(ua);
    const inTikTok =
      /tiktok/.test(ua) ||
      /musical_ly/.test(ua) ||
      /aweme/.test(ua) ||
      /bytedancewebview/.test(ua);
    const inTelegram =
      /telegram|tga|twa/.test(ua) ||
      /t\.me|telegram\.org/.test(document.referrer || "") ||
      !!(window as any).TelegramWebviewProxy ||
      !!(window as any).TelegramWebApp ||
      !!(window as any).Telegram?.WebApp;

    // 使用 WebView 组件但属于独立浏览器，不应识别为 in-app WebView
    const isKnownBrowserUsingWebView =
      /hibrowser|holabrowser|hola/i.test(ua) || // Hola Browser
      /ucbrowser|ucweb/i.test(ua) || // UC Browser
      /brave/i.test(ua) || // Brave
      /duckduckgo/i.test(ua) || // DuckDuckGo
      /mint/i.test(ua); // Mint Browser

    const isWebView =
      !isNativeApp &&
      !isKnownBrowserUsingWebView &&
      (/; wv\)/.test(ua) || // Android WebView 标记
        /\bwv\b/.test(ua) || // WebView 标记
        /version\/\d+.*chrome/.test(ua) || // Android WebView 特征
        // iOS 仅使用明确桥接特征，避免把 Safari/Chrome 误判为 WebView
        !!(window as any).webkit?.messageHandlers?.openExternalBrowser ||
        !!(window as any).webkit?.messageHandlers?.openInBrowser ||
        !!(window as any).flutter_inappwebview || // Flutter WebView
        !!(window as any).ReactNativeWebView || // React Native WebView
        !!(window as any).android || // Android WebView bridge
        !!(window as any).AndroidBridge); // 自定义 Android bridge

    // 所有已知的应用内浏览器（APK 原生环境不算）
    const inKnownAppWebView =
      !isNativeApp &&
      (inFB || inIG || inWeChat || inLine || inTikTok || inTelegram);

    // 通用 WebView 特征
    const inAppWebView = inKnownAppWebView || isWebView;

    const isEdge = /edg\//.test(ua);
    const isOpera = /opr\//.test(ua) || /opera/.test(ua);
    const isSamsung = /samsungbrowser/.test(ua);
    const isFirefox = /firefox/.test(ua);
    const isChromeToken = /chrome\/\d+/.test(ua) || /crios/.test(ua);
    const isChrome =
      isChromeToken &&
      !isEdge &&
      !isOpera &&
      !isSamsung &&
      !isWebView &&
      !isKnownBrowserUsingWebView;

    let browser = "Other";
    if (isNativeApp) browser = "NativeApp";
    else if (inFB) browser = "FacebookInApp";
    else if (inIG) browser = "InstagramInApp";
    else if (inWeChat) browser = "WeChatInApp";
    else if (inLine) browser = "LineInApp";
    else if (inTikTok) browser = "TikTokInApp";
    else if (inTelegram) browser = "TelegramInApp";
    else if (isWebView) browser = "UnknownWebView";
    else if (isKnownBrowserUsingWebView) browser = "ThirdPartyBrowser";
    else if (isSamsung) browser = "Samsung";
    else if (isEdge) browser = "Edge";
    else if (isOpera) browser = "Opera";
    else if (isFirefox) browser = "Firefox";
    else if (isChrome) browser = "Chrome";
    else if (/safari/.test(ua)) browser = "Safari";

    const context = isNativeApp
      ? "NativeApp"
      : inAppWebView
        ? "InAppWebView"
        : "ExternalBrowser";

    return {
      os: isiOS ? "iOS" : isAndroid ? "Android" : "Other",
      browser,
      context,
      referrer: document?.referrer || "",
      isChrome,
      isWebView,
      isNativeApp,
      isTelegram: inTelegram,
      isTikTok: inTikTok,
    };
  }

  supportPwa(): boolean {
    let flag = this.getObject("supportPwa");
    if (flag) return flag;
    flag = !!(window as any).deferredPrompt;
    if (flag) {
      this.setObject("supportPwa", true);
    }
    return flag;
  }
  updateCheckIsPwa() {
    try {
      // in-app WebView（WhatsApp、Instagram 等）的 display-mode 也可能是 standalone，
      // 且可能已有误写入的缓存，必须最先排除
      const envInfo = this.getEnvInfo();
      if (envInfo.context === "InAppWebView") {
        return false;
      }
    } catch {
      // ignore
    }
    const pwa = this.getTmpObject("pwa");
    if (pwa) return true;
    const pp = this.getQueryString("pwa", window.location);
    if ("true" === pp) {
      this.setTmpObject("pwa", true);
      return true;
    }
    try {
      if (
        window.matchMedia("(display-mode: standalone)").matches ||
        (window as any).navigator.standalone === true
      ) {
        this.setTmpObject("pwa", true);
        return true;
      }
    } catch (err) {
      console.error(err);
    }
    return false;
  }

  // 检查PWA是否曾经被安装过（通过localStorage标记）
  // 注意：此方法需要配合 beforeinstallprompt 事件来清除卸载后的残留标记
  checkPwaEverInstalled(): boolean {
    try {
      const installed = localStorage.getItem("pwa_ever_installed");
      return installed === "true";
    } catch (e) {
      console.error("检查PWA安装标记失败:", e);
      return false;
    }
  }

  // 获取PWA安装时间
  getPwaInstalledTime(): number | null {
    try {
      const time = localStorage.getItem("pwa_installed_time");
      return time ? parseInt(time, 10) : null;
    } catch (e) {
      console.error("获取PWA安装时间失败:", e);
      return null;
    }
  }

  // 手动标记PWA已安装（用于兜底超时场景）
  setPwaEverInstalled(): void {
    try {
      localStorage.setItem("pwa_ever_installed", "true");
      if (!localStorage.getItem("pwa_installed_time")) {
        localStorage.setItem("pwa_installed_time", Date.now().toString());
      }
    } catch (e) {
      console.error("标记PWA安装失败:", e);
    }
  }

  // 清除PWA安装标记（用于测试或卸载场景）
  clearPwaInstalledMark(): void {
    try {
      localStorage.removeItem("pwa_ever_installed");
      localStorage.removeItem("pwa_installed_time");
      console.log("已清除PWA安装标记");
    } catch (e) {
      console.error("清除PWA安装标记失败:", e);
    }
  }

  judgeClient(): string {
    const u = navigator.userAgent;
    const isAndroid = u.indexOf("Android") > -1 || u.indexOf("Adr") > -1;
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
    if (isAndroid) {
      return "Android";
    } else if (isIOS) {
      return "IOS";
    } else {
      return "PC";
    }
  }

  isDevicePC(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();

    // 检查是否为移动设备
    const mobileKeywords = [
      "android",
      "webos",
      "iphone",
      "ipad",
      "ipod",
      "blackberry",
      "windows phone",
      "mobile",
      "tablet",
    ];

    const isMobileDevice = mobileKeywords.some(keyword =>
      userAgent.includes(keyword)
    );

    const isTablet =
      userAgent.includes("ipad") ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    if (isMobileDevice || isTablet) {
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;

      if (screenWidth > 1024 || screenHeight > 1024) {
        return true;
      }

      return false;
    }

    return true;
  }

  openWindow(url: string): void {
    // 判断是否是本地开发环境
    const isLocalDev =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.startsWith("192.168.") ||
      window.location.hostname.endsWith(".local");

    if (LtProxy.isActive()) {
      let u = url;
      if (url.indexOf("/") === 0) {
        // 本地开发环境使用本地域名
        if (isLocalDev) {
          u = window.location.origin + url;
        } else {
          u = window.location.origin + url;
        }
      } else if (
        isLocalDev &&
        !url.startsWith("http://") &&
        !url.startsWith("https://")
      ) {
        // 本地开发环境下，如果不是完整URL，使用本地域名
        u = window.location.origin + (url.startsWith("/") ? url : "/" + url);
      } else {
        u = url;
      }

      if (LtProxy.APP_TYPE === "flutter" && LtProxy.isVersionActive(112)) {
        LtProxy.launchUrl(u);
      }
      window.open(u);
    } else {
      let u = url;
      // 本地开发环境处理
      if (isLocalDev && url.indexOf("/") === 0) {
        u = window.location.origin + url;
      }
      window.open(u);
    }
  }

  getCryptoKey(startupData?: any) {
    const sk = startupData && startupData?.sk;
    if (!sk) return null;
    const cryptoKey = sk.split("").reverse().join("");
    return cryptoKey;
  }

  openMiniWindow(url: string, width: number = 375, height: number = 667): void {
    const iTop = (window.screen.height - 30 - height) / 2;
    const iLeft = (window.screen.width - 10 - width) / 2;

    // 判断是否是本地开发环境
    const isLocalDev =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.startsWith("192.168.") ||
      window.location.hostname.endsWith(".local");

    let finalUrl = url;
    // 本地开发环境处理
    if (isLocalDev && url.indexOf("/") === 0) {
      finalUrl = window.location.origin + url;
    }

    if (LtProxy.isActive()) {
      LtProxy.launchUrl(finalUrl);
    } else {
      window.open(
        finalUrl,
        "_blank",
        `width=${width},height=${height},location=no,left=${iLeft},top=${iTop}}`
      );
    }
  }
  formatAmount(amount: number): string | number {
    try {
      return amount === 0
        ? amount
        : this.formatLocaleNumber(parseFloat(amount.toFixed(2)), null, 2, 0);
    } catch {
      return amount;
    }
  }
  formatMinuteTimeSpan(timespan: number, easyMode?: boolean): string {
    const seconds = parseInt((timespan / 1000).toString());
    const second = seconds % 60;
    const minutes = parseInt((seconds / 60).toString());
    const minute = minutes % 60;
    if (easyMode && minute <= 0) {
      return second.toString().padStart(2, "0");
    }
    const ret =
      minute.toString().padStart(2, "0") +
      ":" +
      second.toString().padStart(2, "0");
    return ret;
  }

  formatHourTimeSpan(timespan: number): string {
    const seconds = parseInt((timespan / 1000).toString());
    const second = seconds % 60;
    const minutes = parseInt((seconds / 60).toString());
    const minute = minutes % 60;
    const hours = parseInt((minutes / 60).toString());
    const hour = hours % 60;
    const ret =
      hour.toString().padStart(2, "0") +
      ":" +
      minute.toString().padStart(2, "0") +
      ":" +
      second.toString().padStart(2, "0");
    return ret;
  }

  toFixed(num: number, factor?: number): number {
    if (!num) return num;
    factor = !factor && factor !== 0 ? 1 : factor;
    num = parseFloat("" + num);
    return parseFloat(num.toFixed(factor!));
  }

  private _format: typeof FormatFn | null = null;

  async formatDateTime(
    t: Date | number | string,
    formatStr: string
  ): Promise<string> {
    if (!t) return "";
    if (!(t instanceof Date)) {
      t = new Date(t);
    }
    if (!this._format) {
      const mod = await import("date-fns");
      this._format = mod.format;
    }
    return this._format(t, formatStr);
  }

  getRandomNumber(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  getWeekOfMonth(timestamp: number): number {
    const date = new Date(timestamp);
    const dayOfMonth = date.getDate();
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const weekOfMonth = Math.ceil((dayOfMonth + firstDayOfWeek) / 7);
    return weekOfMonth;
  }

  getWeekString(timestamp: number): string {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const week = this.getWeekOfMonth(timestamp);
    return (
      year.toString() +
      "." +
      month.toString().padStart(2, "0") +
      " " +
      translate("common.week") +
      " " +
      week.toString()
    );
  }

  /**
   * 获取今天的日期范围（秒级时间戳）
   * @returns 包含开始和结束时间戳的对象 { start: number, end: number }
   */
  getTodayRange(): { start: number; end: number } {
    const now = new Date();
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0
    );
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    );
    return {
      start: Math.floor(start.getTime()),
      end: Math.floor(end.getTime()),
    };
  }

  async sleep(t: number): Promise<void> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, t);
    });
  }

  getScaleImageUrl(
    resourceId: string,
    maxWidth: number,
    maxHeight: number
  ): string {
    return this.getImageUrl(resourceId, { width: maxWidth, height: maxHeight });
  }

  // 隐藏初始加载动画
  hideInitialLoading() {
    const loadingElement = document.getElementById("app-loading");
    const rootElement = document.getElementById("root");
    if (loadingElement) {
      loadingElement.classList.add("hidden");
      setTimeout(() => {
        loadingElement.remove();
      }, 300);
    }
    if (rootElement) {
      rootElement.classList.add("loaded");
    }
  }
  showInitialLoading() {
    const loadingElement = document.getElementById("app-loading");
    const rootElement = document.getElementById("root");
    if (loadingElement) {
      loadingElement.classList.remove("hidden");
    } else {
      const newLoadingElement = document.createElement("div");
      newLoadingElement.id = "app-loading";
      document.body.appendChild(newLoadingElement);
    }
    if (rootElement) {
      rootElement.classList.add("loaded");
    } else {
      const newRootElement = document.createElement("div");
      newRootElement.id = "root";
      document.body.appendChild(newRootElement);
    }
  }
  getImageUrl(resourceId: string, props?: ImageUrlProps): string {
    if (!resourceId) {
      return "";
    }
    if (
      resourceId[0] == "/" ||
      resourceId.indexOf("https://") === 0 ||
      resourceId.indexOf("http://") === 0
    ) {
      return resourceId;
    }
    const i = resourceId.indexOf("_");
    if (i > 0) resourceId = resourceId.substring(0, i);
    props = props || {};
    let { width, height, mode } = props;
    width = width || 0;
    height = height || 0;
    if (mode) {
      mode = "." + mode;
    } else {
      mode = "";
    }
    let ret: string;
    const imgVCode = (window as any)._imv || "v2";
    if (!width && !height) {
      ret = `/static/cdnimg/${imgVCode}/${resourceId}${mode}`;
    } else {
      ret = `/static/cdnimg/${imgVCode}/${resourceId}@${width}x${height}${mode}`;
    }
    const imageCdnRoot = config.getImageCdnRoot();
    if (imageCdnRoot) {
      ret = imageCdnRoot + ret;
    }
    return ret;
  }

  getApkDownloadUrl(resId: string): string {
    return `${window.location.origin}/api/static/apk/download?resId=${resId}`;
  }

  /**
   * WebView 内下载 APK：直接在外部浏览器打开下载链接
   */
  openDownloadInBrowser(downloadUrl: string): void {
    this.tryOpenInExternalBrowser(downloadUrl);
  }

  getAudioUrl(resourceId: string): string {
    if (!resourceId) {
      return "/not-audio.wav";
    }
    if (
      resourceId[0] == "/" ||
      resourceId.indexOf("https://") === 0 ||
      resourceId.indexOf("http://") === 0
    ) {
      return resourceId;
    }
    let mode = ".wav";
    if (resourceId.indexOf("_mp3") > 0) {
      mode = ".mp3";
    }
    if (resourceId.indexOf("_wav") > 0) {
      mode = ".wav";
    }
    const i = resourceId.indexOf("_");
    if (i > 0) resourceId = resourceId.substring(0, i);

    const audioVCode = (window as any)._imv || "v2";
    const ret = `/static/cdnaudio/${audioVCode}/${resourceId}${mode}`;
    return ret;
  }

  getVideoUrl(resourceId: string): string {
    if (!resourceId) {
      return "/not-video.mp4";
    }
    if (
      resourceId[0] == "/" ||
      resourceId.indexOf("https://") === 0 ||
      resourceId.indexOf("http://") === 0
    ) {
      return resourceId;
    }
    const i = resourceId.indexOf("_");
    if (i > 0) resourceId = resourceId.substring(0, i);

    const videoVCode = (window as any)._imv || "v2";
    const ret = `/static/cdnvideo/${videoVCode}/${resourceId}.mp4`;
    return ret;
  }

  getUrlParam(url: string, name: string): string | null {
    if (!url) return null;
    const i = url.indexOf("?");
    if (i < 0) return null;
    const reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)", "i");
    const r = url.substr(i + 1).match(reg);
    if (r != null) return r[2];
    return null;
  }

  syncRunning(key: string, runnable: () => Promise<any>): Promise<any> {
    return new Promise(resolve => {
      const run = async () => {
        try {
          while (this.runMap[key]) {
            await this.sleep(100);
          }
          this.runMap[key] = true;
          await runnable();
          resolve(undefined);
        } finally {
          delete this.runMap[key];
        }
      };
      run();
    });
  }

  async getAsCache(path: string, startupData?: any): Promise<any> {
    const cacheKey = startupData && startupData?.designCacheKey;
    if (cacheKey) {
      path = "/api/static/static/apicdn/" + cacheKey + path;
    } else {
      path = "/api" + path;
    }
    // 动态导入避免循环依赖
    const resp = await this.request(path, null, { method: "GET" });
    if (resp && resp.status === 0) {
      const i = path.indexOf("/game/category_page/");
      if (i >= 0) {
        const i2 = i + "/game/category_page/".length;
        const j = path.indexOf("/", i2);
        if (j > i) {
          const cid = path.substring(i2, j);
          if (this.directCids[cid]) {
            if (resp.page && resp.page.content) {
              resp.page.content.forEach((x: any) => {
                if (x.link) {
                  if (x.link.indexOf("?") > 0) {
                    x.link += "&direct=true";
                  } else {
                    x.link += "?direct=true";
                  }
                }
              });
            }
          }
        }
      } else if (path.indexOf("/game/game_category_list") >= 0) {
        const dcids: Record<string, boolean> = {};
        resp.list.forEach((x: any) => {
          if (x.directlyPlay) {
            dcids[x.id] = true;
          }
        });
        this.directCids = dcids;
      }
    }
    return resp;
  }

  getQueryString(name: string, location?: Location): string | null {
    location = location || window.location;
    const reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)", "i");
    const r = location.search.substr(1).match(reg);
    if (r != null) return r[2];
    return null;
  }

  getCookie(cname: string): string {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  postLink(url: string, params: any): void {
    const token = this.getOnceToken();
    const mapForm = document.createElement("form");
    mapForm.target = "Map";
    mapForm.method = "POST";
    mapForm.action = url;

    const ps: Record<string, string> = {
      params: JSON.stringify(params),
      token: token,
    };
    for (const k in ps) {
      const mapInput = document.createElement("input");
      mapInput.type = "text";
      mapInput.name = k;
      mapInput.value = ps[k];
      mapForm.appendChild(mapInput);
    }
    document.body.appendChild(mapForm);

    const map = window.open(
      "",
      "Map",
      "status=0,title=0,height=600,width=800,scrollbars=1"
    );

    if (map) {
      mapForm.submit();
    } else {
      this.createDownloadLink(url, params);
    }
  }

  createDownloadLink(url: string, params: any): string {
    const token = this.getOnceToken();
    let ret = url;
    if (ret.indexOf("?") > 0) {
      ret += "&";
    } else {
      ret += "?";
    }
    ret += "token=" + token;
    if (params) {
      ret += "&params=" + encodeURIComponent(JSON.stringify(params));
    }
    return ret;
  }

  removeToken(url: string): string {
    return requestRemoveToken(url);
  }

  appendToken(url: string): string {
    return requestAppendToken(url);
  }

  appendUrl(url: string, name: string, data: string): string {
    return requestAppendUrl(url, name, data);
  }

  combineObject(obj1: any, obj2: any): any {
    return requestCombineObject(obj1, obj2);
  }

  combineObjects(...args: any[]): any {
    return requestCombineObjects(...args);
  }

  combineArray(nums: number[], k: number): number[][] {
    const arr: number[] = [];
    const ans: number[][] = [];
    function helper(index: number) {
      if (arr.length == k) {
        ans.push(arr.slice());
        return;
      }
      for (let i = index; i < nums.length; i++) {
        arr.push(nums[i]);
        helper(i + 1);
        arr.pop();
      }
    }
    helper(0);
    return ans;
  }

  isMobile(): boolean {
    if (this.forceMobileLayout) return true;
    return window.innerWidth <= 768;
  }

  isPc(): boolean {
    if (this.forceMobileLayout) return false;
    return !this.isMobile();
  }

  deviceIsMobile(): boolean {
    return window.innerWidth <= 768;
  }

  // 获取设备类型
  getDeviceType(): DeviceType {
    return this.deviceIsMobile() ? "mobile" : "pc";
  }
  deviceIsPc(): boolean {
    return !this.deviceIsMobile();
  }

  _getTmpObject(key: string): any {
    return seStorage.getItem(key);
  }

  _setTmpObject(key: string, value: any): void {
    seStorage.setItem(key, value);
  }

  _getObject(key: string): any {
    return storage.getItem(key);
  }

  _setObject(key: string, value: any): void {
    storage.setItem(key, value);
  }

  setLogoutKeepKeys(keys: string[]) {
    this.keepDataKeys = [...this.keepDataKeys, ...keys];
  }
  _clearObject(): void {
    storage.clear(this.keepDataKeys);
  }
  deleteAllCookies() {
    try {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
      }
    } catch {
      // ignore
    }
  }

  getFullLink(link: string): string {
    if (!link) link = "/";
    if (link.indexOf("https://") === 0 || link.indexOf("http://") === 0) {
      return link;
    }
    if (link[0] !== "/") link = "/" + link;
    return window.location.origin + link;
  }

  getApkDownloadLink(startupData?: any): string {
    const apkLink = startupData && startupData?.data?.apkLink;
    if (apkLink) {
      return this.getFullLink(apkLink);
    } else {
      const localApkLink = this.getObject("apkLink");
      return this.getFullLink(localApkLink);
    }
  }

  getObject(key: string): any {
    if (config.storeSessionKey) {
      key = config.storeSessionKey + "-" + key;
    }
    return this._getObject(key);
  }

  getTmpObject(key: string): any {
    (global as any)._tmpcache = (global as any)._tmpcache || {};
    if (!(global as any)._tmpcache[key]) {
      (global as any)._tmpcache[key] = this._getTmpObject(key);
    }
    return (global as any)._tmpcache[key];
  }

  setTmpObject(key: string, value: any): void {
    (global as any)._tmpcache = (global as any)._tmpcache || {};
    (global as any)._tmpcache[key] = value;
    this._setTmpObject(key, value);
  }

  setObject(key: string, value: any): void {
    if (config.storeSessionKey) {
      key = config.storeSessionKey + "-" + key;
    }
    (global as any)._cache = (global as any)._cache || {};
    (global as any)._cache[key] = value;
    this._setObject(key, value);
  }

  clearObject(): void {
    (global as any)._cache = {};
    const loginData = this.getObject("login-data");
    this._clearObject();
    this.setObject("login-data", loginData);
  }

  getUser(): any {
    const ret = this.getObject("xx-user-to");
    if (!ret) return null;

    if (typeof ret === "string") {
      return ret;
    } else if (typeof ret === "object") {
      return JSON.stringify(ret);
    }

    return ret;
  }

  brPhoneFormat(value: string): boolean {
    const reg = /^[1-9]{2}-?[2-9]{1}\d{3,4}-?\d{4}$/;
    return reg.test(value);
  }

  setUser(user: any, props?: any): void {
    if (props) {
      user = { ...user, ...props };
    }
    this.setObject("xx-user-to", JSON.stringify(user));
    if (config.onUser) {
      config.onUser(user);
    }
  }

  getBrowserLanguage(): string {
    return navigator.language || (navigator as any).userLanguage || "";
  }

  isApp(): boolean {
    return (
      !!LtProxy.isActive() ||
      !!this.getObject("gaid") ||
      !!this.getObject("aid") ||
      !!this.getObject("inApp") ||
      !!this.getObject("gpApp")
    );
  }

  isGpApp(): boolean {
    return !!this.getObject("gpApp");
  }

  getAppVersion(): string | null {
    // 从localStorage获取应用版本
    return this.getObject("version") || null;
  }

  getServerTime(): number {
    if (window.__serverTime) {
      return window.__serverTime();
    }
    return new Date().getTime();
  }

  getOnceToken(): string {
    // 获取用户token，用于认证
    const user = this.getObject("xx-user-to");
    return user?.token || "";
  }

  isSoundOff(): boolean {
    return this.getObject("soundOff") || false;
  }
  setSoundOff(off: boolean) {
    this.setObject("soundOff", off || false);
  }
  playSound(asset: string) {
    if (!asset) return;
    if (this.isSoundOff()) return;
    if (!asset.includes("http")) {
      asset = window.location.origin + asset;
    }
    const audio = window._newAudio(asset);
    audio.play();
  }
  getDesignStyle(key: string) {
    const color = getComputedStyle(document.documentElement).getPropertyValue(
      key
    );
    return color;
  }
  setDesignStyle(key: string, style: string) {
    document.documentElement.style.setProperty(key, style);
  }
  setAllDesignStyle(obj: Record<string, any>) {
    for (const k in obj) {
      if (obj[k]) this.setDesignStyle(k, obj[k]);
    }
  }
  async uploadFile(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    const uploadUrl = "/api/file/upload?image=false";

    return apiRequest(uploadUrl, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000,
    });
  }

  async request(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<RequestResponse> {
    return apiRequest(url, data, options);
  }

  /**
   * 根据投放渠道 ch 拉取邀请人 agentId 并写入缓存（供注册页邀请码使用）
   * GET /api/home_display/launch_agent?id=xx，后台 @RequestParam("id") 接收
   */
  async fetchLaunchAgentAndCache(ch: string): Promise<void> {
    if (!ch || !ch.trim()) return;
    try {
      const id = encodeURIComponent(ch.trim());
      const resp = await this.request(
        `/api/home_display/launch_agent?id=${id}`,
        null,
        { method: "GET", alert: false }
      );
      const model = (resp as any)?.data?.model ?? (resp as any)?.model;
      const agentId = model?.agentId;
      if (agentId && typeof agentId === "string") {
        this.setObject("launchAgentId", agentId);
      }
    } catch {
      // 静默失败，不影响主流程
    }
  }

  //判断属性是否存在
  hasProperty(obj: any, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }
  downloadLink(src: string) {
    const form = document.createElement("form");
    try {
      const url = new URL(src);
      const params = new URLSearchParams(url.search);
      for (const pair of params.entries()) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = pair[0];
        input.value = pair[1];
        form.appendChild(input);
      }
    } catch {
      // ignore
    }
    form.action = src;
    document.getElementsByTagName("body")[0].appendChild(form);
    form.submit();
    setTimeout(() => {
      document.body.removeChild(form);
    }, 1500);
  }
  // openExternalPreferChrome(url: string): boolean {
  //   try {
  //     const ua = (navigator.userAgent || "").toLowerCase();
  //     const isAndroid = /android/.test(ua);
  //     if (!isAndroid) return false;

  //     // 检测是否在 Facebook/Instagram 等会拦截的环境
  //     const inFB = /fbav|fban|fb_iab/.test(ua);
  //     const inIG = /instagram/.test(ua);
  //     const inRestrictedEnv = inFB || inIG;

  //     const stripped = url.replace(/^https?:\/\//, "");
  //     let leftPage = false;
  //     let attempted = false; // 标记是否已经尝试过

  //     const handleLeave = () => {
  //       leftPage = true;
  //       cleanup();
  //     };
  //     const onHidden = () => {
  //       if (document.visibilityState === "hidden") handleLeave();
  //     };
  //     const cleanup = () => {
  //       document.removeEventListener("visibilitychange", onHidden);
  //       window.removeEventListener("pagehide", handleLeave);
  //       clearTimeout(t1);
  //       clearTimeout(t2);
  //       clearTimeout(t3);
  //     };
  //     document.addEventListener("visibilitychange", onHidden);
  //     window.addEventListener("pagehide", handleLeave);

  //     const intentWithComponent =
  //       `intent://${stripped}` +
  //       `#Intent;scheme=https;action=android.intent.action.VIEW;` +
  //       `package=com.android.chrome;component=com.android.chrome/com.google.android.apps.chrome.Main;` +
  //       `S.browser_fallback_url=${encodeURIComponent(url)};end`;

  //     const intentSimple =
  //       `intent://${stripped}` +
  //       `#Intent;scheme=https;action=android.intent.action.VIEW;` +
  //       `package=com.android.chrome;` +
  //       `S.browser_fallback_url=${encodeURIComponent(url)};end`;

  //     const chromeNavigate = `googlechrome://navigate?url=${encodeURIComponent(url)}`;

  //     const tryOpenTargets = (link: string) => {
  //       if (leftPage || attempted) return;
  //       attempted = true;

  //       try {
  //         window.location.href = link;
  //       } catch {
  //         // ignore
  //       }

  //       // FB/IG 环境：不重置 attempted，避免快速连续调用
  //       if (inRestrictedEnv) {
  //         setTimeout(() => {
  //           if (leftPage) {
  //             cleanup();
  //           }
  //         }, 50);
  //       } else {
  //         setTimeout(() => {
  //           if (!leftPage) {
  //             attempted = false;
  //           } else {
  //             cleanup();
  //           }
  //         }, 50);
  //       }
  //     };

  //     if (inRestrictedEnv) {
  //       tryOpenTargets(intentWithComponent);
  //       setTimeout(() => {
  //         if (!leftPage) {
  //           cleanup();
  //         }
  //       }, 1500);
  //       return true;
  //     }

  //     tryOpenTargets(intentWithComponent);

  //     const t1 = setTimeout(() => {
  //       if (leftPage) return;
  //       tryOpenTargets(intentSimple);
  //     }, 200);

  //     const t2 = setTimeout(() => {
  //       if (leftPage) return;
  //       tryOpenTargets(chromeNavigate);
  //     }, 400);

  //     const t3 = setTimeout(() => {
  //       if (leftPage) return;
  //       cleanup();
  //       (window as any).__openBrowserGuide__?.();
  //     }, 1200);

  //     return true;
  //   } catch {
  //     return false;
  //   }
  // }
  openExternalPreferChrome(url: string): boolean {
    try {
      const ua = (navigator.userAgent || "").toLowerCase();
      const isAndroid = /android/.test(ua);
      if (!isAndroid) return false;

      const stripped = url.replace(/^https?:\/\//, "");

      // 方案 1：不指定包名的 intent，让系统弹出浏览器选择器
      const intentDefault =
        `intent://${stripped}` +
        `#Intent;scheme=https;action=android.intent.action.VIEW;end`;

      // 方案 2：用 <a target="_blank"> 打开普通 URL，触发系统浏览器
      const tryOpenViaLink = () => {
        try {
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            try {
              document.body.removeChild(a);
            } catch {
              /* ignore */
            }
          }, 100);
        } catch {
          window.open(url, "_blank");
        }
      };

      let left = false;
      const onVisChange = () => {
        if (document.visibilityState === "hidden") left = true;
      };
      document.addEventListener("visibilitychange", onVisChange);

      const cleanup = () => {
        document.removeEventListener("visibilitychange", onVisChange);
      };

      // 先尝试 intent（部分 WebView 支持）
      try {
        window.location.href = intentDefault;
      } catch {
        /* ignore */
      }

      // 800ms 后如果页面还在前台，说明 intent 失败，用 <a> 标签兜底
      setTimeout(() => {
        cleanup();
        if (!left) {
          tryOpenViaLink();
        }
      }, 800);

      return true;
    } catch {
      return false;
    }
  }
  openExternalBrowserIOS(url: string): boolean {
    try {
      const env = this.getEnvInfo();
      if (env.context === "InAppWebView") {
        const attemptKey = `ios_external_open_attempted:${url}`;
        if (sessionStorage.getItem(attemptKey) === "1") {
          return false;
        }
        sessionStorage.setItem(attemptKey, "1");

        // 在 WebView 中，尝试使用原生桥接
        if ((window as any).webkit?.messageHandlers?.openExternalBrowser) {
          (
            window as any
          ).webkit.messageHandlers.openExternalBrowser.postMessage(url);
          return true;
        }
        if ((window as any).webkit?.messageHandlers?.openInBrowser) {
          (window as any).webkit.messageHandlers.openInBrowser.postMessage({
            url: url,
          });
          return true;
        }
        if ((window as any).openExternalBrowser) {
          (window as any).openExternalBrowser(url);
          return true;
        }

        // 先尝试 Safari，失败后再尝试 Chrome；两者都失败再释放防重试
        const safariUrl = url.replace(/^https?:\/\//, "x-safari-https://");
        const chromeUrl = url.replace(/^https?:\/\//, "googlechrome://");
        let leftPage = false;
        const timers: {
          t1?: number;
          t2?: number;
          t3?: number;
        } = {};
        const handleLeave = () => {
          leftPage = true;
          cleanup();
        };
        const onHidden = () => {
          if (document.visibilityState === "hidden") {
            handleLeave();
          }
        };
        const cleanup = () => {
          document.removeEventListener("visibilitychange", onHidden);
          window.removeEventListener("pagehide", handleLeave);
          if (timers.t1) clearTimeout(timers.t1);
          if (timers.t2) clearTimeout(timers.t2);
          if (timers.t3) clearTimeout(timers.t3);
        };
        document.addEventListener("visibilitychange", onHidden);
        window.addEventListener("pagehide", handleLeave);

        try {
          window.location.href = safariUrl;
        } catch {
          // ignore
        }

        timers.t1 = window.setTimeout(() => {
          if (leftPage) return;
          try {
            window.location.href = chromeUrl;
          } catch {
            // ignore
          }
        }, 1200);

        timers.t2 = window.setTimeout(() => {
          if (leftPage) return;
          // Safari 与 Chrome 均未离开当前页，释放锁避免误伤后续手动触发
          sessionStorage.removeItem(attemptKey);
          cleanup();
        }, 3200);

        timers.t3 = window.setTimeout(() => {
          if (leftPage) return;
          // 兜底释放，防止极端情况下未正确清理
          sessionStorage.removeItem(attemptKey);
        }, 5000);

        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  openFromTelegram(url: string): boolean {
    try {
      const tgApp =
        (window as any).TelegramWebApp || (window as any).Telegram?.WebApp;
      if (tgApp && typeof tgApp.openLink === "function") {
        tgApp.openLink(url, { try_instant_view: false });
        return true;
      }
    } catch {
      // ignore
    }
    return this.openExternalPreferChrome(url);
  }

  isInChrome() {
    try {
      const userAgent = navigator.userAgent.toLowerCase();
      const isChrome =
        (/chrome/.test(userAgent) || /crios/.test(userAgent)) &&
        !/edg/.test(userAgent) &&
        !/opr\//.test(userAgent);
      return isChrome;
    } catch {
      return false;
    }
  }

  /**
   * 尝试通过 intent 跳转到 Chrome 浏览器（仅 Android 非 Chrome 时生效）
   *
   * 通过 visibilitychange + pagehide 判断是否真正离开了页面：
   * - 有 Chrome + 选是 → 页面离开（hidden / pagehide），不再回来
   * - 有 Chrome + 选否 → 页面 hidden → visible，触发 onCancelled
   * - 没有 Chrome → 页面始终未离开，超时后触发 onNotInstalled
   *
   * @param onCancelled 有 Chrome 但用户选"否"时的回调
   * @param onNotInstalled 没有安装 Chrome 时的回调
   * @returns true 表示已发起跳转尝试，false 表示不需要跳转
   */
  redirectToChrome(
    onCancelled?: () => void,
    onNotInstalled?: () => void
  ): boolean {
    const ua = navigator.userAgent || "";
    const isAndroid = /android/i.test(ua);
    if (!isAndroid) return false;

    const envInfo = this.getEnvInfo();
    if (envInfo.isChrome) return false;

    const stripped = window.location.href.replace(/^https?:\/\//, "");
    const intentUrl =
      `intent://${stripped}` +
      `#Intent;scheme=https;action=android.intent.action.VIEW;` +
      `package=com.android.chrome;end`;

    let leftPage = false;
    const timers: { notInstalled?: number; cleanup?: number } = {};

    const handleLeave = () => {
      leftPage = true;
      cleanup();
    };

    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        handleLeave();
      } else if (leftPage && document.visibilityState === "visible") {
        leftPage = false;
        cleanup();
        onCancelled?.();
      }
    };

    const cleanup = () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", handleLeave);
      if (timers.notInstalled) clearTimeout(timers.notInstalled);
      if (timers.cleanup) clearTimeout(timers.cleanup);
    };

    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", handleLeave);

    try {
      const a = document.createElement("a");
      a.href = intentUrl;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try {
          document.body.removeChild(a);
        } catch {
          /* ignore */
        }
      }, 100);
    } catch {
      cleanup();
      return false;
    }

    // 2s 后如果页面始终未离开过，说明没有 Chrome（系统没弹任何东西）
    timers.notInstalled = window.setTimeout(() => {
      if (leftPage) return;
      cleanup();
      onNotInstalled?.();
    }, 2000);

    // 5s 兜底清理，防止极端情况下事件监听未正确移除
    timers.cleanup = window.setTimeout(() => {
      cleanup();
    }, 5000);

    return true;
  }

  openPwaApp() {
    const urlParams = new URLSearchParams(window.location.search);
    const chParam = urlParams.get("ch");
    const c = urlParams.get("c");
    const p = urlParams.get("p");
    // 构建PWA链接，如果有ch参数则拼接上去
    let pwaLink = window.location.origin + "/?pwa=true";
    if (chParam) {
      pwaLink += "&ch=" + chParam;
    }
    if (c) {
      pwaLink += "&c=" + c;
    }
    if (p) {
      pwaLink += "&p=" + p;
    }
    window.open(pwaLink, "_blank");
  }

  installIosApp(startupData?: any) {
    if (startupData && startupData.iosMobileFileLink) {
      window.open(startupData.iosMobileFileLink, "_blank");
    }
  }

  isIoInstall() {
    return "standalone" in window.navigator && window.navigator.standalone;
  }
  // 语言常量
  private static readonly LOCALE_MAP = {
    pt: "pt-BR",
    id: "id-ID",
    en: "en",
  } as const;

  // 缓存货币信息，避免重复查找
  private currencyCache = new Map<string, { decimalLength: number } | null>();

  formatLocaleNumber(
    num: number,
    currencyId: string | null,
    numFixed?: number,
    minFixed?: number
  ): string | number {
    return this._formatLocaleNumber(num, currencyId, numFixed, minFixed);
  }
  numberToNumberText(labelValue: number | string): string {
    // Nine Zeroes for Billions
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? (Math.abs(Number(labelValue)) / 1.0e9).toFixed(0) +
          " " +
          translate("common.billion")
      : // Six Zeroes for Millions
        Math.abs(Number(labelValue)) >= 1.0e6
        ? (Math.abs(Number(labelValue)) / 1.0e6).toFixed(0) +
          " " +
          translate("common.million")
        : // Three Zeroes for Thousands
          Math.abs(Number(labelValue)) >= 1.0e3
          ? (Math.abs(Number(labelValue)) / 1.0e3).toFixed(0) +
            " " +
            translate("common.k")
          : String(Math.abs(Number(labelValue))) || "0";
  }
  numberToKFormat(number: number, currencyId: string | null): string | number {
    try {
      if (number < 1000) {
        return this.formatLocaleNumber(number, currencyId);
      }
      const formattedNum = (number / 1000).toFixed(1);
      if (formattedNum.endsWith(".0")) {
        return (
          this.formatLocaleNumber(
            Number(formattedNum.slice(0, -2)),
            currencyId
          ) + "K"
        );
      }
      return this.formatLocaleNumber(Number(formattedNum), currencyId) + "K";
    } catch {
      return number;
    }
  }
  formatLocaleNumberForHead(
    num: number,
    isShowTwoDecimals: boolean | string | number,
    numFixed: number,
    minFixed: number
  ): string | number {
    // 优化布尔值判断逻辑
    if (isShowTwoDecimals === true) {
      return this._formatLocaleNumber(num, null, 2, 2);
    }
    if (isShowTwoDecimals === false) {
      return this._formatLocaleNumber(num, null, 0, 0);
    }
    return this._formatLocaleNumber(
      num,
      typeof isShowTwoDecimals === "string" ? isShowTwoDecimals : null,
      numFixed,
      minFixed
    );
  }

  private _formatLocaleNumber(
    num: number,
    currencyId: string | null,
    numFixed?: number,
    minFixed?: number
  ): string | number {
    try {
      // 提前验证数字
      if (isNaN(num)) return 0;
      const n = Number(num);
      if (isNaN(n)) return num;

      const language = getCurrentLanguage();
      const fixed = numFixed ?? this.getCurrencyFixed(currencyId);

      // 处理数字精度
      const processedNum = this._processNumber(n, fixed);

      // 获取语言代码
      const locale = this._getLocaleCode(language);

      return processedNum.toLocaleString(locale, {
        maximumFractionDigits: fixed,
        minimumFractionDigits: minFixed,
      });
    } catch (error) {
      console.warn("Number formatting error:", error);
      return num;
    }
  }

  formatLocaleNumberV2(
    num: number,
    currencyId: string | null
  ): string | number {
    try {
      if (isNaN(num)) return 0;
      const n = Number(num);
      if (isNaN(n)) return num;

      const fixed = this.getCurrencyFixed(currencyId);
      const processedNum = this._processNumber(n, fixed);
      const language = getCurrentLanguage();
      const locale = this._getLocaleCode(language);

      return processedNum.toLocaleString(locale, {
        maximumFractionDigits: fixed,
      });
    } catch (error) {
      console.warn("Number formatting V2 error:", error);
      return num;
    }
  }

  private _processNumber(num: number, fixed: number): number {
    const nonExponential = this.toNonExponential(num);
    return (
      Math.floor(nonExponential * Math.pow(10, fixed)) / Math.pow(10, fixed)
    );
  }

  private _getLocaleCode(language: string): string {
    if (language.startsWith("pt")) return Utils.LOCALE_MAP.pt;
    if (language.startsWith("id")) return Utils.LOCALE_MAP.id;
    return Utils.LOCALE_MAP.en;
  }

  getCurrencyFixed(currencyId: string | null, startupData?: any): number {
    try {
      // 使用缓存避免重复查找
      const cacheKey = currencyId || "default";
      if (this.currencyCache.has(cacheKey)) {
        const cached = this.currencyCache.get(cacheKey);
        return cached?.decimalLength ?? 9;
      }

      const currency =
        this.getCurrency(currencyId, startupData) ||
        this.getCurrency(startupData?.currencyId || null, startupData);

      const decimalLength = currency?.decimalLength ?? 9;

      // 缓存结果
      this.currencyCache.set(cacheKey, currency ? { decimalLength } : null);

      return decimalLength;
    } catch (error) {
      console.warn("Currency fixed lookup error:", error);
      return 2;
    }
  }

  getCurrency(
    currencyId: string | null,
    startupData?: any
  ): { decimalLength: number } | null {
    if (!currencyId) return null;

    try {
      const currencies = startupData?.currencies;
      if (!currencies?.length) return null;

      return currencies.find((c: any) => c.id === currencyId) || null;
    } catch (error) {
      console.warn("Currency lookup error:", error);
      return null;
    }
  }

  toNonExponential(num: number): number {
    try {
      const exponential = num.toExponential();
      const match = exponential.match(/\d(?:\.(\d*))?e([+-]\d+)/);

      if (!match) return num;

      const [, decimalPart, exponentPart] = match;
      const decimalLength = decimalPart?.length || 0;
      const exponent = parseInt(exponentPart, 10);

      return Number(num.toFixed(Math.max(0, decimalLength - exponent)));
    } catch (error) {
      console.warn("Non-exponential conversion error:", error);
      return num;
    }
  }

  /**
   * 精确除法（类似 Java BigDecimal.divide）
   * @param dividend 被除数
   * @param divisor 除数
   * @param scale 小数位数，默认 4
   * @param roundingMode HALF_DOWN: 四舍六入五向下；HALF_UP: 四舍五入
   */
  preciseDivide(
    dividend: number,
    divisor: number,
    scale = 4,
    roundingMode: "HALF_DOWN" | "HALF_UP" = "HALF_DOWN"
  ): number {
    if (!divisor || divisor === 0) return 0;
    const raw = dividend / divisor;
    const factor = Math.pow(10, scale);
    const scaled = raw * factor;
    if (roundingMode === "HALF_DOWN") {
      const floored = Math.floor(scaled);
      const frac = scaled - floored;
      const rounded = frac < 0.5 ? floored : frac > 0.5 ? floored + 1 : floored;
      return rounded / factor;
    }
    return Math.round(scaled) / factor;
  }

  // 清理货币缓存的方法
  clearCurrencyCache(): void {
    this.currencyCache.clear();
  }
  getMd5(content: string) {
    return md5(content);
  }

  /**
   * 格式化日期为 MM/DD/YYYY 格式
   * @param timestamp 时间戳（毫秒）
   * @returns 格式化后的日期字符串
   */
  formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  replaceLocaleList(contentList: any[]) {
    let template = "";
    let i = 1;
    const mm: Record<string, any> = {};
    for (const c of contentList) {
      if (c.var) {
        template += "##@" + i + "##";
        mm[`@${i}`] = c;
        i += 1;
      } else {
        template += c.content;
      }
    }
    if (!this.localeList[template]) {
      this.localeList[template] = true;
    }
    return locale.render(template, items => {
      return items
        .map(x => {
          if (!x) return null;
          const vv = mm[x];
          if (vv) {
            vv.content = translate(vv.content);
            return vv;
          }
          return { content: x };
        })
        .filter(x => x);
    });
  }

  /**
   * 检查并尝试在外部浏览器中打开当前页面
   * 如果在内置浏览器中，尝试打开外部浏览器并返回 true
   * 否则返回 false
   * @param url 要打开的URL，默认为当前页面URL
   * @returns 是否成功触发外部浏览器跳转
   */
  tryOpenInExternalBrowser(url?: string): boolean {
    const targetUrl = url || window.location.href;
    const envInfo = this.getEnvInfo();

    if (envInfo.context === "InAppWebView") {
      if (envInfo.os === "iOS") {
        this.openExternalBrowserIOS(targetUrl);
      } else {
        setTimeout(() => {
          this.openExternalPreferChrome(targetUrl);
        }, 0);
      }
      return true;
    }

    return false;
  }

  /**
   * 设置 cookie
   * @param name cookie 名称
   * @param value cookie 值
   * @param days 有效天数
   */
  setCookie(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/`;
  }
}

const utils = new Utils();
export default utils;
